import { GatewayConfig, InfraHealth, ToolCall } from '../types';
import { INITIAL_INFRA_HEALTH, INITIAL_TOOL_CALLS, generateMockToolCall } from './mockData';

const CONFIG_STORAGE_KEY = 'computer_controller_gateway_config_v1';

export const DEFAULT_CONFIG: GatewayConfig = {
  baseUrl: 'https://9router.hanavy.online',
  bearerToken: '',
  isDemoMode: true, // Defaults to true if no token, can be toggled
};

export function getGatewayConfig(): GatewayConfig {
  try {
    if (typeof window !== 'undefined' && window.location?.search) {
      const params = new URLSearchParams(window.location.search);
      const urlToken = params.get('token');
      const urlBase = params.get('gateway');
      if (urlToken || urlBase) {
        const raw = localStorage.getItem(CONFIG_STORAGE_KEY);
        const existing = raw ? JSON.parse(raw) : DEFAULT_CONFIG;
        const updated: GatewayConfig = {
          baseUrl: urlBase || existing.baseUrl || DEFAULT_CONFIG.baseUrl,
          bearerToken: urlToken !== null ? urlToken : (existing.bearerToken || ''),
          isDemoMode: false
        };
        saveGatewayConfig(updated);
        try {
          const cleanUrl = window.location.pathname + window.location.hash;
          window.history.replaceState({}, document.title, cleanUrl);
        } catch {}
        return updated;
      }
    }

    const raw = localStorage.getItem(CONFIG_STORAGE_KEY);
    if (!raw) return DEFAULT_CONFIG;
    const parsed = JSON.parse(raw);
    return {
      baseUrl: parsed.baseUrl || DEFAULT_CONFIG.baseUrl,
      bearerToken: parsed.bearerToken || '',
      isDemoMode: parsed.isDemoMode !== undefined ? parsed.isDemoMode : (!parsed.bearerToken)
    };
  } catch {
    return DEFAULT_CONFIG;
  }
}

export function saveGatewayConfig(config: GatewayConfig): void {
  try {
    localStorage.setItem(CONFIG_STORAGE_KEY, JSON.stringify(config));
  } catch (e) {
    console.error('Failed to persist gateway config to localStorage:', e);
  }
}

export interface PollResult {
  toolCalls: ToolCall[];
  infraHealth: InfraHealth;
  isSimulated: boolean;
  statusMessage: string;
  serverPingMs?: number;
}

export async function testGatewayConnection(baseUrl: string, token: string): Promise<{
  success: boolean;
  latencyMs: number;
  message: string;
  statusCode?: number;
}> {
  const cleanUrl = baseUrl.replace(/\/+$/, '');
  const startTime = performance.now();

  try {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 6000);

    const headers: Record<string, string> = {
      'Accept': 'application/json',
    };
    if (token) {
      headers['Authorization'] = `Bearer ${token.trim()}`;
    }

    // Attempt pinging /api/dashboard/overview if token provided, else fallback to /healthz or /
    const testEndpoint = token ? `${cleanUrl}/api/dashboard/overview` : `${cleanUrl}/healthz`;
    const response = await fetch(testEndpoint, {
      method: 'GET',
      headers,
      signal: controller.signal
    }).catch(async () => {
      return await fetch(`${cleanUrl}/healthz`, {
        method: 'GET',
        headers,
        signal: controller.signal
      }).catch(async () => {
        return await fetch(`${cleanUrl}/`, {
          method: 'GET',
          headers,
          signal: controller.signal
        });
      });
    });

    clearTimeout(timeoutId);
    const latencyMs = Math.round(performance.now() - startTime);

    if (response.ok) {
      return {
        success: true,
        latencyMs,
        statusCode: response.status,
        message: `Connected successfully (${latencyMs}ms, HTTP ${response.status})`
      };
    } else if (response.status === 401 || response.status === 403) {
      return {
        success: false,
        latencyMs,
        statusCode: response.status,
        message: `Authentication failed (HTTP ${response.status}). Check your Bearer token.`
      };
    } else {
      return {
        success: false,
        latencyMs,
        statusCode: response.status,
        message: `Gateway responded with HTTP ${response.status}: ${response.statusText}`
      };
    }
  } catch (err: any) {
    const latencyMs = Math.round(performance.now() - startTime);
    if (err.name === 'AbortError') {
      return {
        success: false,
        latencyMs,
        message: 'Connection timed out after 6 seconds.'
      };
    }
    return {
      success: false,
      latencyMs,
      message: err.message || 'Unable to connect to gateway endpoint (check CORS / DNS).'
    };
  }
}

export async function fetchTelemetryData(
  config: GatewayConfig,
  currentCalls: ToolCall[]
): Promise<PollResult> {
  const isSimulation = config.isDemoMode || !config.bearerToken;

  if (isSimulation) {
    // Generate an authentic telemetry update
    const shouldAddNew = Math.random() > 0.3; // 70% chance of new tool call on each tick
    let updatedCalls = [...currentCalls];

    if (shouldAddNew || updatedCalls.length === 0) {
      const newCall = generateMockToolCall();
      // Keep up to 100 recent calls
      updatedCalls = [newCall, ...updatedCalls.slice(0, 99)];
    }

    const jitterPing = Math.floor(Math.random() * 8) - 4;
    const updatedInfra: InfraHealth = {
      ...INITIAL_INFRA_HEALTH,
      vpsLatencyMs: Math.max(10, INITIAL_INFRA_HEALTH.vpsLatencyMs + jitterPing),
      macLatencyMs: Math.max(14, INITIAL_INFRA_HEALTH.macLatencyMs + jitterPing),
      lastCheckIso: new Date().toISOString()
    };

    return {
      toolCalls: updatedCalls,
      infraHealth: updatedInfra,
      isSimulated: true,
      statusMessage: config.bearerToken
        ? 'Demo Mode Active (Synthetic Stream)'
        : 'Setup Required: Bearer token not configured',
      serverPingMs: updatedInfra.vpsLatencyMs
    };
  }

  // Attempt real remote fetch
  const cleanUrl = config.baseUrl.replace(/\/+$/, '');
  const startTime = performance.now();

  try {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 4500);

    const headers: Record<string, string> = {
      'Accept': 'application/json',
      'Authorization': `Bearer ${config.bearerToken.trim()}`
    };

    const response = await fetch(`${cleanUrl}/api/dashboard/overview`, {
      method: 'GET',
      headers,
      signal: controller.signal
    });

    clearTimeout(timeoutId);
    const latencyMs = Math.round(performance.now() - startTime);

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }

    const json = await response.json();

    // Map remote events from /api/dashboard/overview
    let remoteCalls: ToolCall[] = [];
    if (Array.isArray(json.recent_events)) {
      remoteCalls = json.recent_events.map((ev: any, idx: number) => {
        const isMac = ev.host_target === 'mac' || (ev.endpoint && ev.endpoint.includes('/api/mac/'));
        const statusCode = ev.status_code || 200;
        
        let toolName = 'gateway_call';
        const ep = ev.endpoint || '';
        if (ep.includes('/api/mac/screenshot')) toolName = 'mac_screenshot';
        else if (ep.includes('/api/mac/ui_tree') || ep.includes('/api/mac/find_element')) toolName = 'mac_ui_tree';
        else if (ep.includes('/api/mac/click')) toolName = 'mac_click';
        else if (ep.includes('/api/mac/type')) toolName = 'mac_type';
        else if (ep.includes('/api/mac/')) toolName = 'mac_agent';
        else if (ep.includes('/api/shell/exec')) toolName = 'shell_exec';
        else if (ep.includes('/api/shell/background')) toolName = 'shell_background';
        else if (ep.includes('/api/files/write')) toolName = 'files_write';
        else if (ep.includes('/api/files/read')) toolName = 'files_read';
        else if (ep.includes('/api/files/stat')) toolName = 'files_stat';
        else if (ep.includes('/api/files/patch')) toolName = 'files_patch';
        else if (ep.includes('/api/git/')) toolName = 'git_ops';
        else if (ep.includes('/api/antigravity/')) toolName = 'antigravity';
        else if (ep.includes('/mcp')) toolName = 'mcp_stream';
        else if (ep.includes('/health')) toolName = 'system_health';
        else if (ev.action) toolName = ev.action.split(' ')[1] || 'gateway_call';

        let securityAudit = undefined;
        if (statusCode === 403) {
          securityAudit = {
            ruleTriggered: 'POLICY_INTERCEPTION',
            policyId: 'SEC-DENY-GUARD-01',
            riskLevel: 'HIGH' as const,
            violationReason: ev.error || 'Blocked by security policy guardrail (unauthorized privilege or path containment)',
            mitigationAction: 'Execution rejected with HTTP 403 Forbidden',
            blockedPattern: ev.details?.blocked_command || ev.action
          };
        }

        // Extract HTTP method
        let httpMethod: string | undefined = ev.method;
        if (!httpMethod && ev.action) {
          const firstToken = ev.action.trim().split(/\s+/)[0];
          if (['GET', 'POST', 'PUT', 'DELETE', 'PATCH', 'OPTIONS', 'HEAD'].includes(firstToken.toUpperCase())) {
            httpMethod = firstToken.toUpperCase();
          }
        }
        if (!httpMethod) {
          httpMethod = ep.startsWith('/health') || ep.includes('/overview') || ep.includes('/read') || ep.includes('/stat') ? 'GET' : 'POST';
        }

        // Extract correlation Request ID
        const requestId: string | undefined = ev.details?.request_id || ev.request_id || undefined;

        // Extract Response Bytes metric
        const rawBytes = ev.details?.response_bytes ?? ev.response_bytes;
        const responseBytes: number | undefined = typeof rawBytes === 'number' ? rawBytes : undefined;

        // Extract Query Parameters
        let queryParams: Record<string, string> | undefined = undefined;
        if (ev.details?.query_params && typeof ev.details.query_params === 'object') {
          queryParams = { ...ev.details.query_params };
        } else if (ev.query_params && typeof ev.query_params === 'object') {
          queryParams = { ...ev.query_params };
        } else if (ep.includes('?')) {
          try {
            const searchPart = ep.substring(ep.indexOf('?'));
            const searchParams = new URLSearchParams(searchPart);
            const parsed: Record<string, string> = {};
            searchParams.forEach((val, key) => {
              parsed[key] = val;
            });
            if (Object.keys(parsed).length > 0) {
              queryParams = parsed;
            }
          } catch {}
        }

        // Cleanly extract tool call input parameters from params, parameters, or fallback details
        let parameters: Record<string, any> = {};
        if (ev.details?.params && typeof ev.details.params === 'object') {
          parameters = { ...ev.details.params };
        } else if (ev.details?.parameters && typeof ev.details.parameters === 'object') {
          parameters = { ...ev.details.parameters };
        } else if (ev.params && typeof ev.params === 'object') {
          parameters = { ...ev.params };
        } else if (ev.details && typeof ev.details === 'object') {
          // Fallback filtering
          const filtered: Record<string, any> = {};
          for (const [key, value] of Object.entries(ev.details)) {
            if (!['request_id', 'response_bytes', 'query_params', 'blocked_command'].includes(key)) {
              filtered[key] = value;
            }
          }
          if (Object.keys(filtered).length > 0) {
            parameters = filtered;
          }
        }

        // Real response payload only (never assign { response_bytes: ... })
        let responsePayload: Record<string, any> | string | undefined = undefined;
        if (ev.response_payload !== undefined) {
          responsePayload = ev.response_payload;
        } else if (ev.response_body !== undefined) {
          responsePayload = ev.response_body;
        } else if (ev.details?.response_payload !== undefined) {
          responsePayload = ev.details.response_payload;
        } else if (ev.details?.response_body !== undefined) {
          responsePayload = ev.details.response_body;
        }

        return {
          id: requestId || `call_${Math.floor(ev.unix_time || Date.now() / 1000)}_${idx}`,
          timestamp: ev.timestamp || new Date().toISOString(),
          toolName,
          action: ev.action || `${httpMethod} ${ep}`,
          endpoint: ep || '/api/gateway',
          targetNode: isMac ? 'Mac' : 'VPS',
          nodeDetails: {
            os: isMac ? 'macOS (Apple Silicon)' : 'Ubuntu 24.04 LTS (x86_64)',
            arch: isMac ? 'arm64' : 'x86_64',
            host: isMac ? 'Apple Silicon Mac (Tailscale)' : `Azure VPS (${ev.host_target || 'vps'})`,
            ip: ev.client_ip || '100.125.196.110'
          },
          status: statusCode,
          statusText: statusCode === 200 ? 'OK' : statusCode === 403 ? 'Forbidden' : statusCode === 409 ? 'Conflict' : 'Error',
          durationMs: typeof ev.duration_ms === 'number' ? Math.round(ev.duration_ms) : 25,
          clientIp: ev.client_ip || '127.0.0.1',
          parameters,
          errorMessage: ev.error || undefined,
          responsePayload,
          securityAudit,
          httpMethod,
          responseBytes,
          requestId,
          queryParams
        };
      });
    } else {
      remoteCalls = currentCalls;
    }

    const remoteInfra: InfraHealth = {
      vpsStatus: json.gateway?.status === 'online' ? 'HEALTHY' : 'DEGRADED',
      vpsLatencyMs: latencyMs,
      vpsRegion: 'Azure Cloud (West US)',
      vpsCpuLoad: json.hosts?.vps?.cpu_percent || 14.2,
      vpsMemoryPct: json.hosts?.vps?.memory_percent || 32.5,
      macStatus: json.hosts?.mac?.status === 'healthy' || json.hosts?.mac?.status === 'online' ? 'ONLINE' : 'STANDBY',
      macLatencyMs: Math.max(12, Math.round(latencyMs * 0.8)),
      macTailscaleIp: '100.125.196.110',
      macDeviceModel: 'Mac (Apple Silicon)',
      macOsVersion: 'macOS 15.x',
      uptimePercent: 99.98,
      uptimeString: `${Math.floor((json.gateway?.uptime_seconds || 3600) / 3600)}h ${Math.floor(((json.gateway?.uptime_seconds || 3600) % 3600) / 60)}m`,
      lastCheckIso: new Date().toISOString()
    };

    return {
      toolCalls: remoteCalls,
      infraHealth: remoteInfra,
      isSimulated: false,
      statusMessage: `Live Connected to ${new URL(cleanUrl).hostname} (${latencyMs}ms)`,
      serverPingMs: latencyMs
    };
  } catch (error: any) {
    // If live sync fails, report error without churning fake destructive calls
    return {
      toolCalls: currentCalls,
      infraHealth: {
        ...INITIAL_INFRA_HEALTH,
        vpsStatus: 'DEGRADED',
        lastCheckIso: new Date().toISOString()
      },
      isSimulated: true,
      statusMessage: `Live sync error: ${error.message || 'Remote gateway unreachable'}`
    };
  }
}
